/**
 * Overlay Component
 *
 * Handles parsing and formatting of overlay structures.
 * Similar to component structure: contains input interface, parser, and formatter.
 */

import { v4 as uuidv4 } from 'uuid'

import {
  IOverlay,
  OverlayCloseBehavior,
  OverlayPlacement,
} from '@makyoapp/makyo-sdk/models/AppBuilder.js'

import type { AiComponentInput } from '../components/registry'
import type { AppBreakpoint } from '../components/types'
import { deepMerge, parseResponsiveFormat } from '../components/parser-utils'
import { parseAiComponents } from '../components/parse-components'
import { formatComponents } from '../components/format-components'
import overlayTemplate from './overlay.template.json'

/**
 * Simplified AI Overlay Input
 */
export interface AiOverlayInput {
  type: 'overlay'
  tableId?: string
  title: string
  icon?: string
  backgroundColor?: string
  components: AiComponentInput[]
  placement?: string // Format: "bottom ta:left de:right"
  size?: string // Format: "w:100% ta:w:80% de:w:60%"
  shape?: string // Format: "medium ta:large"
  closeBehavior?: 'direct' | 'back'
  opacity?: number // 0-100
}

/**
 * Create slug from text
 */
function createSlug(text: string, defaultSlug: string = ''): string {
  let preformatted = text.trimEnd() + (text.endsWith(' ') ? '-' : '')
  const slug = preformatted
    .toLowerCase()
    .normalize('NFD')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')

  return slug || defaultSlug
}

/**
 * Extract icon name from icon format string
 * Format: "icon:rocket_launch" -> "rocket_launch"
 * If no prefix, returns as-is
 */
function extractIconName(icon: string | undefined): string {
  if (!icon) return 'draft'
  if (icon.startsWith('icon:')) {
    return icon.substring(5) // Remove "icon:" prefix
  }
  return icon
}

/**
 * Format icon name and format it
 * Converts icon name to format: "icon:rocket_launch" or returns as-is
 */
function formatIconName(icon: { value: string } | undefined): string | undefined {
  if (!icon || !icon.value) return undefined
  const iconName = icon.value
  // If it doesn't start with "icon:", add the prefix
  if (!iconName.startsWith('icon:')) {
    return `icon:${iconName}`
  }
  return iconName
}

/**
 * Parse placement format string
 * Format: "<default> <breakpoint>:<value>"
 * Example: "bottom ta:left de:right"
 */
function parsePlacementFormat(format: string): Record<AppBreakpoint, OverlayPlacement> {
  const parsed = parseResponsiveFormat(format, (value) => {
    const normalized = value.toLowerCase().trim()
    if (['top', 'bottom', 'left', 'right'].includes(normalized)) {
      return normalized as OverlayPlacement
    }
    return OverlayPlacement.Bottom
  })

  return {
    phone: parsed.phone,
    'phone-landscape': parsed['phone-landscape'],
    tablet: parsed.tablet,
    'tablet-landscape': parsed['tablet-landscape'],
    desktop: parsed.desktop,
    tv: parsed.tv,
  }
}

/**
 * Format placement back to string format
 */
function formatPlacement(placement: Record<AppBreakpoint, OverlayPlacement>): string | undefined {
  // Check if all values are the same
  const values = Object.values(placement)
  const firstValue = values[0]
  if (values.every((v) => v === firstValue)) {
    return firstValue === OverlayPlacement.Bottom ? undefined : firstValue
  }

  // Build responsive format string
  const parts: string[] = []
  if (placement.phone !== OverlayPlacement.Bottom) {
    parts.push(placement.phone)
  }
  if (placement.tablet !== placement.phone) {
    parts.push(`ta:${placement.tablet}`)
  }
  if (placement.desktop !== placement.tablet) {
    parts.push(`de:${placement.desktop}`)
  }

  return parts.length > 0 ? parts.join(' ') : undefined
}

/**
 * Parse size format string
 * Format: "<default>" or responsive: "w:90% ta:w:50%"
 * The size should be in format "width, height" where height defaults to "100%"
 */
function parseSizeFormat(format: string): Record<AppBreakpoint, string> {
  const parsed = parseResponsiveFormat(format, (value) => {
    // Remove "w:" prefix if present
    let cleanValue = value.replace(/^w:/, '')
    
    // If it's already a valid size string with comma (width, height), return it
    if (cleanValue.includes(',')) {
      return cleanValue
    }
    // If it's just width, add ", 100%" for height
    if (cleanValue.includes('%') || cleanValue.includes('px')) {
      return `${cleanValue}, 100%`
    }
    // Default to 100%, 100%
    return '100%, 100%'
  })

  return {
    phone: parsed.phone,
    'phone-landscape': parsed['phone-landscape'],
    tablet: parsed.tablet,
    'tablet-landscape': parsed['tablet-landscape'],
    desktop: parsed.desktop,
    tv: parsed.tv,
  }
}

/**
 * Format size back to string format
 */
function formatSize(size: Record<AppBreakpoint, string>): string | undefined {
  // Extract width from "width, height" format
  const extractWidth = (value: string) => {
    if (value.includes(',')) {
      return value.split(',')[0].trim()
    }
    return value
  }

  const widths = {
    phone: extractWidth(size.phone),
    tablet: extractWidth(size.tablet),
    desktop: extractWidth(size.desktop),
  }

  // Check if all widths are the same
  if (widths.phone === widths.tablet && widths.tablet === widths.desktop) {
    return widths.phone === '100%' ? undefined : `w:${widths.phone}`
  }

  // Build responsive format string
  const parts: string[] = []
  if (widths.phone !== '100%') {
    parts.push(`w:${widths.phone}`)
  }
  if (widths.tablet !== widths.phone) {
    parts.push(`ta:w:${widths.tablet}`)
  }
  if (widths.desktop !== widths.tablet) {
    parts.push(`de:w:${widths.desktop}`)
  }

  return parts.length > 0 ? parts.join(' ') : undefined
}

/**
 * Parse shape format string
 * Format: "<default> <breakpoint>:<value>"
 * Example: "medium ta:large"
 */
function parseShapeFormat(format: string): Record<AppBreakpoint, string> {
  const parsed = parseResponsiveFormat(format, (value) => {
    const normalized = value.toLowerCase().trim()
    if (['none', 'small', 'medium', 'large'].includes(normalized)) {
      return normalized
    }
    return 'medium'
  })

  return {
    phone: parsed.phone,
    'phone-landscape': parsed['phone-landscape'],
    tablet: parsed.tablet,
    'tablet-landscape': parsed['tablet-landscape'],
    desktop: parsed.desktop,
    tv: parsed.tv,
  }
}

/**
 * Format shape back to string format
 */
function formatShape(shape: Record<AppBreakpoint, string>): string | undefined {
  // Check if all values are the same
  const values = Object.values(shape)
  const firstValue = values[0]
  if (values.every((v) => v === firstValue)) {
    return firstValue === 'medium' ? undefined : firstValue
  }

  // Build responsive format string
  const parts: string[] = []
  if (shape.phone !== 'medium') {
    parts.push(shape.phone)
  }
  if (shape.tablet !== shape.phone) {
    parts.push(`ta:${shape.tablet}`)
  }
  if (shape.desktop !== shape.tablet) {
    parts.push(`de:${shape.desktop}`)
  }

  return parts.length > 0 ? parts.join(' ') : undefined
}

const OVERLAY_DEFAULT_TEMPLATE = overlayTemplate as unknown as IOverlay

/**
 * Parse AI overlay input to full overlay structure
 */
export function parseAiOverlay(input: AiOverlayInput): IOverlay {
  // Start with template defaults
  const result = deepMerge(OVERLAY_DEFAULT_TEMPLATE, {})

  // Generate new ID and slug
  result._id = uuidv4()
  result._Slug = createSlug(input.title)

  // Create _Body structure (nested) and _BodySettings (flat)
  const { bodies, bodySettings } = parseAiComponents(input.components)
  result._Body = bodies
  result._BodySettings = bodySettings

  // Parse and override settings
  if (input.tableId) {
    result._TableID = input.tableId
  }

  if (input.title) {
    result._Settings._Title = input.title
  }

  if (input.icon) {
    result._Settings._Icon = { value: extractIconName(input.icon) }
  }

  if (input.backgroundColor) {
    result._Settings._BackgroundColor = input.backgroundColor
  }

  // Parse responsive values
  if (input.placement) {
    const placement = parsePlacementFormat(input.placement)
    result._Placement = placement
  }

  if (input.size) {
    const size = parseSizeFormat(input.size)
    result._Size = size
  }

  if (input.shape) {
    const shape = parseShapeFormat(input.shape)
    result._Shape = shape
  }

  // Close behavior
  if (input.closeBehavior === 'back') {
    result._CloseBehavior = OverlayCloseBehavior.Back
  }

  // Opacity
  if (input.opacity !== undefined) {
    result._Opacity = input.opacity
  }

  return result
}

/**
 * Format IOverlay to AiOverlayInput
 */
export function formatAiOverlay(overlay: IOverlay): AiOverlayInput {
  const icon = overlay._Settings?._Icon ? formatIconName({ value: overlay._Settings._Icon.value }) : undefined
  const backgroundColor = overlay._Settings?._BackgroundColor
  const title = overlay._Settings?._Title || ''

  // Format components from _Body and _BodySettings
  const components = formatComponents(overlay._Body || [], overlay._BodySettings || {})

  // Format responsive values
  const placement = formatPlacement(overlay._Placement)
  const size = formatSize(overlay._Size)
  const shape = formatShape(overlay._Shape)

  // Format close behavior
  const closeBehavior =
    overlay._CloseBehavior === OverlayCloseBehavior.Back ? 'back' : 'direct'

  // Build result object with components last (for readability)
  // Collect optional properties first
  const optionalProps: Partial<AiOverlayInput> = {}
  
  if (overlay._TableID) {
    optionalProps.tableId = overlay._TableID
  }
  if (icon) {
    optionalProps.icon = icon
  }
  if (backgroundColor && backgroundColor !== 'primary-95') {
    optionalProps.backgroundColor = backgroundColor
  }
  if (placement) {
    optionalProps.placement = placement
  }
  if (size) {
    optionalProps.size = size
  }
  if (shape) {
    optionalProps.shape = shape
  }
  if (closeBehavior !== 'direct') {
    optionalProps.closeBehavior = closeBehavior
  }
  if (overlay._Opacity !== undefined && overlay._Opacity !== 100) {
    optionalProps.opacity = overlay._Opacity
  }

  // Build final object with components last (for readability, matching component container pattern)
  // Using object literal with explicit property order ensures JSON.stringify preserves order
  const result: AiOverlayInput = {
    type: 'overlay',
    title,
    ...optionalProps,
    components, // components comes last
  }

  return result
}

